// Basic
import PropTypes from 'prop-types';
// Libs
import { DateTime } from 'luxon';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
// Components
import Icon from '@/common-utils/components/renders/icon/Icon';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Badge } from '@/common-utils/components/ui/badge';
// Common
import ICONS from '@/common-utils/enums/icons';
import SUBSCRIPTION_STATUS from '../../enums/SubscriptionStatus';
import subscriptionFields from '../../fields/subscriptionFields';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
// Lang
import { nsSubscription } from '@/app/i18n/namespaces';

/**
 *
 * @param root0
 * @param root0.cellValue
 * @param root0.row
 * @returns {JSX} SubscriptionBadge
 */
function SubscriptionBadge({ cellValue, row }) {
    // Constants
    const rowData = row.original;
    const subscriptionStatus = SUBSCRIPTION_STATUS[cellValue];
    const clientUuid = getObjectAttributeFromStringKey(rowData, generateNestedFieldAccessor(subscriptionFields.CLIENT.UUID));
    const resiliationDate = getObjectAttributeFromStringKey(rowData, generateNestedFieldAccessor(subscriptionFields.RESILIATION_DATE));
    const isResiliationDateInFuture = resiliationDate ? DateTime.fromISO(resiliationDate) > DateTime.now() : false;
    const isMigrating = rowData ?
        getObjectAttributeFromStringKey(rowData, generateNestedFieldAccessor(subscriptionFields.IS_MIGRATING))
        : false;



    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    let tooltip;
    switch (subscriptionStatus.name) {
        case SUBSCRIPTION_STATUS.IN_STOCK.name: {
            if (clientUuid === rootOrganization.uuid) {
                if (isResiliationDateInFuture) {
                    tooltip = 'tooltip.badge.restock';
                } else {
                    tooltip = 'tooltip.badge.activate';
                    row.class = 'error-row';
                }
                break;
            }
            if (isResiliationDateInFuture) {
                tooltip = 'tooltip.badge.terminate';
                row.class = 'warning-row';
                break;
            }
            break;
        }
        case SUBSCRIPTION_STATUS.ACTIVATED.name: {
            if (isResiliationDateInFuture) {
                tooltip = 'tooltip.badge.terminate';
                row.class = 'warning-row';
            }
            break;
        }
        case SUBSCRIPTION_STATUS.TERMINATED.name: {
            if (clientUuid === rootOrganization.uuid) {
                tooltip = 'tooltip.badge.restock';
                row.class = 'error-row';
            }
            break;
        }
        default: tooltip = undefined;
    }

    return (
        subscriptionStatus &&
        <div className='flex items-center gap-1'>
            <Badge entity={subscriptionStatus.entity} namespace={subscriptionStatus.namespace} variant={subscriptionStatus.variant} />
            {
                isMigrating &&
                <TooltipHelper
                    trigger={<Icon icon={ICONS.MIGRATE} />}
                    triggerClass={'flex m-0'}
                    contentClass='max-w-xs whitespace-normal'
                    content={<Entity
                        entity={'tooltip.badge.migrate'}
                        namespace={nsSubscription}
                    />}
                />
            }
            {
                tooltip &&
                <TooltipHelper
                    trigger={<Icon icon={ICONS.UNKNOWN} />}
                    triggerClass={'flex m-0'}
                    contentClass='max-w-xs whitespace-normal'
                    content={<Entity
                        entity={tooltip}
                        namespace={nsSubscription}
                        data={isResiliationDateInFuture ? { resiliationDate: DateTime.fromISO(resiliationDate).toLocaleString() } : undefined}
                    />}
                />
            }
        </div>
    );
}

SubscriptionBadge.propTypes = {
    cellValue: PropTypes.string.isRequired,
    row: PropTypes.object.isRequired
};

export default SubscriptionBadge;